'use client';

import { TechnologyOption } from '@/lib/tech-stack-types';
import { prepareHeatmapData, getScoreColor, getCategoryDisplayName } from '@/lib/visualization-data';
import { useState, useMemo } from 'react';

interface TechStackHeatmapProps {
  technologies: TechnologyOption[];
}

type SortColumn = 'name' | 'compliance' | 'technical' | 'operational' | 'cost' | 'ecosystem' | 'maturity';
type SortDirection = 'asc' | 'desc';

const DIMENSIONS = [
  { key: 'compliance', label: 'Compliance', description: 'Regulatory compliance support' },
  { key: 'technical', label: 'Technical', description: 'Performance, scalability, and ease of use' },
  { key: 'operational', label: 'Operations', description: 'Maintenance and monitoring complexity' },
  { key: 'cost', label: 'Cost', description: 'Pricing and vendor lock-in' },
  { key: 'ecosystem', label: 'Ecosystem', description: 'Community and documentation quality' },
  { key: 'maturity', label: 'Maturity', description: 'Production readiness' },
] as const;

export default function TechStackHeatmap({ technologies }: TechStackHeatmapProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{ techId: string; dimension: string } | null>(null);

  const heatmapData = useMemo(() => prepareHeatmapData(technologies), [technologies]);
  const categories = useMemo(
    () => ['all', ...new Set(technologies.map(t => t.category))],
    [technologies]
  );

  // Filter by category
  const filteredData = useMemo(() => {
    if (!selectedCategory || selectedCategory === 'all') {
      return heatmapData.technologies;
    }
    return heatmapData.technologies.filter(t => t.category === selectedCategory);
  }, [heatmapData, selectedCategory]);

  // Sort data
  const sortedData = useMemo(() => {
    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      let aValue: any = a.name;
      let bValue: any = b.name;

      if (sortColumn !== 'name') {
        aValue = a.scores[sortColumn as keyof typeof a.scores];
        bValue = b.scores[sortColumn as keyof typeof b.scores];
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    return sorted;
  }, [filteredData, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc'); // Default to descending for scores
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="space-y-6">
      {/* Title and Description */}
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Comparison Heatmap</h3>
        <p className="text-foreground-secondary text-sm">
          Compare technologies across all evaluation dimensions.
          Green = high score (good), Yellow = medium, Red = low score (concerning).
        </p>
      </div>

      {/* Category Filter */}
      <div>
        <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
          Filter by Category
        </label>
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                (selectedCategory === category) || (category === 'all' && selectedCategory === null)
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-surface text-foreground-secondary hover:bg-surface/80'
              }`}
            >
              {category === 'all' ? 'All Categories' : getCategoryDisplayName(category)}
            </button>
          ))}
        </div>
      </div>

      {/* Color Legend */}
      <div className="bg-surface rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-3">Score Legend</h4>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded" style={{ backgroundColor: '#EF4444' }} />
            <span className="text-xs text-foreground-secondary">0-59 (Low)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded" style={{ backgroundColor: '#F59E0B' }} />
            <span className="text-xs text-foreground-secondary">60-79 (Medium)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded" style={{ backgroundColor: '#10B981' }} />
            <span className="text-xs text-foreground-secondary">80-100 (High)</span>
          </div>
        </div>
      </div>

      {/* Heatmap Table */}
      <div className="bg-surface rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-background">
                <th className="sticky left-0 z-20 bg-background">
                  <button
                    onClick={() => handleSort('name')}
                    className="w-full text-left px-4 py-3 text-sm font-semibold text-foreground hover:text-accent transition-colors"
                  >
                    Technology {getSortIcon('name')}
                  </button>
                </th>
                {DIMENSIONS.map(dim => (
                  <th key={dim.key}>
                    <button
                      onClick={() => handleSort(dim.key as SortColumn)}
                      className="w-full px-3 py-3 text-sm font-semibold text-foreground hover:text-accent transition-colors"
                      title={dim.description}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span>{dim.label}</span>
                        <span className="text-xs opacity-60">{getSortIcon(dim.key as SortColumn)}</span>
                      </div>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={DIMENSIONS.length + 1} className="px-4 py-8 text-center text-foreground-tertiary">
                    No technologies found in this category
                  </td>
                </tr>
              ) : (
                sortedData.map((tech, rowIndex) => (
                  <tr
                    key={tech.id}
                    className={`border-t border-border-muted hover:bg-background/50 transition-colors ${
                      rowIndex % 2 === 0 ? 'bg-background/20' : ''
                    }`}
                  >
                    <td className="sticky left-0 z-10 bg-surface px-4 py-3">
                      <div className="font-medium text-foreground">{tech.name}</div>
                      <div className="text-xs text-foreground-tertiary mt-0.5">
                        {getCategoryDisplayName(tech.category)}
                      </div>
                    </td>
                    {DIMENSIONS.map(dim => {
                      const score = tech.scores[dim.key as keyof typeof tech.scores];
                      const isHovered = hoveredCell?.techId === tech.id && hoveredCell?.dimension === dim.key;

                      return (
                        <td
                          key={dim.key}
                          className="px-3 py-3 text-center relative"
                          onMouseEnter={() => setHoveredCell({ techId: tech.id, dimension: dim.key })}
                          onMouseLeave={() => setHoveredCell(null)}
                        >
                          <div
                            className={`inline-flex items-center justify-center w-16 h-10 rounded font-semibold text-sm transition-all ${
                              isHovered ? 'ring-2 ring-cream scale-110' : ''
                            }`}
                            style={{
                              backgroundColor: getScoreColor(score),
                              color: score >= 60 ? '#0A1E2E' : '#E8D5B7',
                            }}
                          >
                            {score}
                          </div>
                          {isHovered && (
                            <div className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-2 bg-background border border-border rounded-lg p-2 shadow-lg whitespace-nowrap">
                              <div className="text-xs font-semibold text-foreground">{dim.label}</div>
                              <div className="text-xs text-foreground-secondary">{dim.description}</div>
                              <div className="text-sm font-bold text-accent mt-1">{score}/100</div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Statistics */}
      <div className="bg-surface rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-4">Category Statistics</h4>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
          {DIMENSIONS.map(dim => {
            if (sortedData.length === 0) return null;

            const scores = sortedData.map(t => t.scores[dim.key as keyof typeof t.scores]);
            const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
            const max = Math.max(...scores);
            const min = Math.min(...scores);

            return (
              <div key={dim.key} className="bg-background rounded-lg p-4">
                <div className="text-xs font-semibold text-foreground-secondary mb-2">{dim.label}</div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-tertiary">Avg:</span>
                    <span className="text-foreground font-semibold">{avg.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-tertiary">Max:</span>
                    <span className="text-green-500 font-semibold">{max}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground-tertiary">Min:</span>
                    <span className="text-red-500 font-semibold">{min}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-surface/50 border border-border rounded-lg p-4">
        <p className="text-xs text-foreground-secondary">
          <strong className="text-foreground">Pro Tip:</strong> Click column headers to sort by that dimension.
          Hover over cells to see detailed score breakdowns. Use category filters to focus on specific technology types.
        </p>
      </div>
    </div>
  );
}
